// SettingsProfilesPane.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Utilities
import { buildOverrideFieldsFromOrder, makeOrderedFieldPayload, moveOrderedFieldByName } from 'utilities/NodeFieldReorder';
import { createSchemaField, insertSchemaFieldAtVisibleIndex } from 'utilities/NodeSchemaFields';
import { generateKey } from 'utilities/Keys';
import { pluralize } from 'utilities/Strings';

// Services
import {
    saveWorkspaceProfileSchema,
    subscribeToWorkspaceProfileSchema,
} from 'services/workspaces/WorkspaceProfileSchemaService';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';
import useFieldModalController from 'node/form/hooks/useFieldModalController';

// CSS
import 'database/typefields/TypeFields.css';

// Components
import DatabaseSettingsAddButton from 'database/settings/addbutton/DatabaseSettingsAddButton';
import TypeFieldsFieldRow from 'database/typefields/fieldrow/TypeFieldsFieldRow';
import FieldModal from 'node/form/fieldmodal/FieldModal';

const WORKSPACE_MEMBER_PROFILE_FIELD_TYPES = [
    'string',
    'phone',
    'email',
    'enum',
    'multiselect',
    'people',
    'annualdate',
    'date',
    'daterange',
    'datetime',
    'datetimerange',
    'boolean',
    'number',
    'percentage',
    'currency',
    'url',
];

/**
 * SettingsProfilesPane
 *
 * Renders the space member profile schema editor inside Settings.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object|null} workspace - Active workspace model.
 * @param {object|null} user - Current signed-in user.
 *
 * @returns {ReactNode} The rendered profiles settings pane.
 */
const SettingsProfilesPane = React.memo(({
    theme,
    workspace,
    user,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [draggingFieldName, setDraggingFieldName] = useState('');
    const [dragOverFieldName, setDragOverFieldName] = useState('');

    // ----- MEMOS ----- //

    const workspaceKey = useMemo(() => String(workspace?.key || '').trim(), [workspace?.key]);
    const userKey = useMemo(() => String(user?.key || '').trim(), [user?.key]);
    const subtitle = useMemo(() => {
        if (fields.length === 0) {
            return 'Define custom workspace-only fields for member profiles.';
        }

        return `${fields.length} profile ${pluralize('field', fields.length)} configured for this workspace.`;
    }, [fields]);

    // ----- HOOKS ----- //

    const {
        fieldModalOpen,
        fieldInsertIndex,
        fieldEditingField,
        handleFieldModalOpen,
        handleAddFieldClick,
        handleSettingsClick,
    } = useFieldModalController({ canEdit: !saving });

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!workspaceKey) {
            setFields([]);
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        setErrorMessage('');

        return subscribeToWorkspaceProfileSchema(
            workspaceKey,
            (schemaDoc) => {
                setFields(Array.isArray(schemaDoc?.schema?.fields) ? schemaDoc.schema.fields : []);
                setLoading(false);
            },
            (error) => {
                console.error('[SettingsProfilesPane] Failed to load profile schema:', error);
                setErrorMessage('Unable to load profile fields right now.');
                setLoading(false);
            }
        );
    }, [workspaceKey]);

    // ----- HELPERS ----- //

    const persistFields = useCallback(async (nextFields, successMessage) => {
        if (!workspaceKey) {
            setErrorMessage('Workspace not ready.');
            return false;
        }

        if (!userKey) {
            setErrorMessage('User not ready.');
            return false;
        }

        setSaving(true);
        setErrorMessage('');

        try {
            await saveWorkspaceProfileSchema({
                workspaceKey,
                userKey,
                fields: nextFields,
            });

            setStatusMessage(successMessage);
            return true;
        } catch (error) {
            console.error('[SettingsProfilesPane] Failed to save profile schema:', error);
            setErrorMessage(error?.message || 'Unable to save profile fields right now.');
            return false;
        } finally {
            setSaving(false);
        }
    }, [userKey, workspaceKey]);

    const buildAddedField = useCallback((fieldDraft) => {
        return createSchemaField({
            name: generateKey(),
            label: fieldDraft.label,
            fieldDescription: fieldDraft.fieldDescription,
            type: fieldDraft.type,
            targetType: fieldDraft.targetType,
            templateKeys: fieldDraft.templateKeys,
            defaultValue: fieldDraft.default,
            defaultUnit: fieldDraft.defaultUnit,
            options: fieldDraft.options,
            enumOptionsSort: fieldDraft.enumOptionsSort,
            inherited: false,
        });
    }, []);

    const buildUpdatedField = useCallback((fieldDraft) => {
        if (!fieldEditingField?.name) return null;

        const nextField = {
            ...fieldEditingField,
            label: fieldDraft.label,
            fieldDescription: String(fieldDraft.fieldDescription || '').trim(),
            default: fieldDraft.default,
        };

        if (fieldEditingField.type === 'enum' || fieldEditingField.type === 'multiselect') {
            nextField.options = fieldDraft.options;
        }

        if (fieldEditingField.type === 'enum') {
            nextField.enumOptionsSort = fieldDraft.enumOptionsSort;
        }

        if (!nextField.fieldDescription) {
            delete nextField.fieldDescription;
        }

        return nextField;
    }, [fieldEditingField]);

    // ----- HANDLERS ----- //

    const handleFieldSave = useCallback(async (fieldDraft) => {
        if (!fieldDraft?.label || !fieldDraft?.type || saving) return;

        const nextField = buildAddedField(fieldDraft);
        const nextFields = insertSchemaFieldAtVisibleIndex(fields, fields, fieldInsertIndex, nextField);

        setFields(nextFields);
        const didSave = await persistFields(nextFields, 'Profile schema saved.');

        if (didSave) {
            handleFieldModalOpen(false);
            return;
        }

        setFields(fields);
    }, [buildAddedField, fieldInsertIndex, fields, handleFieldModalOpen, persistFields, saving]);

    const handleFieldUpdate = useCallback(async (fieldDraft) => {
        if (!fieldEditingField?.name || saving) return;

        const nextField = buildUpdatedField(fieldDraft);
        if (!nextField) return;

        const nextFields = fields.map((field) => (
            field?.name === fieldEditingField.name
                ? nextField
                : field
        ));

        setFields(nextFields);
        const didSave = await persistFields(nextFields, 'Profile schema saved.');

        if (didSave) {
            handleFieldModalOpen(false);
            return;
        }

        setFields(fields);
    }, [buildUpdatedField, fieldEditingField, fields, handleFieldModalOpen, persistFields, saving]);

    const handleFieldDelete = useCallback(async () => {
        if (!fieldEditingField?.name || saving) return;

        const nextFields = fields.filter((field) => field?.name !== fieldEditingField.name);

        setFields(nextFields);
        const didSave = await persistFields(nextFields, 'Field deleted.');

        if (didSave) {
            handleFieldModalOpen(false);
            return;
        }

        setFields(fields);
    }, [fieldEditingField, fields, handleFieldModalOpen, persistFields, saving]);

    const handleDragStart = useCallback((field, event) => {
        if (!field?.name || saving) return;

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', field.name);
        setDraggingFieldName(field.name);
    }, [saving]);

    const handleDragOver = useCallback((field, event) => {
        if (!draggingFieldName || !field?.name || saving) return;
        if (field.name === draggingFieldName) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragOverFieldName(field.name);
    }, [draggingFieldName, saving]);

    const handleDragEnd = useCallback(() => {
        setDraggingFieldName('');
        setDragOverFieldName('');
    }, []);

    const handleDrop = useCallback(async (field, event) => {
        if (!draggingFieldName || !field?.name || saving) return;

        event.preventDefault();

        if (field.name === draggingFieldName) {
            handleDragEnd();
            return;
        }

        const previousFields = fields;
        const displayOrder = makeOrderedFieldPayload(fields);
        const nextOrder = moveOrderedFieldByName(displayOrder, draggingFieldName, field.name);

        if (!nextOrder) {
            handleDragEnd();
            return;
        }

        const nextFields = buildOverrideFieldsFromOrder({
            ordered: nextOrder,
            resolvedFields: fields,
            fields,
        });

        setFields(nextFields);
        handleDragEnd();

        const didSave = await persistFields(nextFields, 'Field order updated.');
        if (!didSave) {
            setFields(previousFields);
        }
    }, [draggingFieldName, fields, handleDragEnd, persistFields, saving]);

    // ----- RENDER ----- //

    return (
        <>
            <div
                className="settings-modal-pane-header"
                style={{
                    padding: isMobile ? '6px' : '0px',
                    paddingRight: 20,
                }}
            >

                <div className="settings-modal-pane-header-left">
                    <div
                        className="settings-modal-pane-title"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        Profiles
                    </div>

                    <div
                        className="settings-modal-pane-subtitle"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        {subtitle}
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">

                {errorMessage ? (
                    <div className="settings-modal-error">{errorMessage}</div>
                ) : null}

                {statusMessage && !errorMessage ? (
                    <div className="settings-modal-status">{statusMessage}</div>
                ) : null}

                <div className="type-fields-container"
                    style={{
                        paddingLeft: "0px",
                        paddingRight: "0px",
                    }}>
                    {loading ? (
                        <div
                            className="type-fields-empty"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Loading profile fields...
                        </div>
                    ) : (
                        <>
                            <DatabaseSettingsAddButton
                                title="Add Field"
                                disabled={saving}
                                onClick={() => handleAddFieldClick(0)}
                            />

                            {fields.map((field) => {
                                const isDragging = draggingFieldName === field.name;
                                const isDropTarget = dragOverFieldName === field.name;

                                return (
                                    <TypeFieldsFieldRow
                                        key={field.name}
                                        field={field}
                                        isDragging={isDragging}
                                        isDropTarget={isDropTarget}
                                        isSystemType={false}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(event) => handleDragOver(field, event)}
                                        onDragStart={(event) => handleDragStart(field, event)}
                                        onDrop={(event) => handleDrop(field, event)}
                                        onSettingsClick={() => handleSettingsClick(field)}
                                    />
                                );
                            })}

                            {fields.length > 0 ? (
                                <DatabaseSettingsAddButton
                                    title="Add Field"
                                    disabled={saving}
                                    onClick={() => handleAddFieldClick(fields.length)}
                                />
                            ) : (
                                <div
                                    className="type-fields-empty"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    No profile fields yet. Add one to get started.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <FieldModal
                isOpen={fieldModalOpen}
                setOpen={handleFieldModalOpen}
                onSave={fieldEditingField ? handleFieldUpdate : handleFieldSave}
                onDelete={handleFieldDelete}
                isSaving={saving}
                editingField={fieldEditingField}
                nodeTypeLabel="Space Member Profile"
                existingFields={fields}
                workspaceKey={workspaceKey}
                allowedFieldTypes={WORKSPACE_MEMBER_PROFILE_FIELD_TYPES}
            />
        </>
    );
});

// Dev Tools
SettingsProfilesPane.displayName = 'SettingsProfilesPane';

export default SettingsProfilesPane;
